import { SectorMap } from "./symbols";
import { formatPattern } from "./util-filter";

/**
 * Groups symbol entries by their sector from SectorMap
 * Preserve one primary group per symbol, while allowing duplicate membership
 * only across matching hyphenated sub-sector groups.
 * @param {Array} symbolEntries - Array of [symbol, pattern] tuples
 * @param {string} selectedSector - If selected, omit cross-listed symbols from its own sub-sector group
 * @returns {Object} - Grouped object where keys are sector names and values are arrays of [symbol, pattern]
 */
export const groupSymbolsBySector = (symbolEntries, selectedSector = "") => {
  const grouped = {};

  symbolEntries.forEach(([sym, pat]) => {
    const normalizedSym = String(sym || "").toLowerCase();
    const foundSectors = [];

    // Find all sectors this symbol belongs to
    for (const [sectorKey, sectorSymbols] of Object.entries(SectorMap)) {
      if (
        sectorSymbols.some(
          (sectorSymbol) => String(sectorSymbol).toLowerCase() === normalizedSym,
        )
      ) {
        foundSectors.push(sectorKey);
      }
    }

    const primarySector = foundSectors.length
      ? foundSectors.reduce((longest, current) =>
          current.length > longest.length ? current : longest,
        )
      : null;
    const subSectors = foundSectors.filter((sector) => sector.includes("-"));
    const groupKeys = primarySector
      ? Array.from(new Set([primarySector, ...subSectors]))
      : ["other"];
    groupKeys.forEach((groupKey) => {
      const isSelectedSubSector =
        groupKey === selectedSector && groupKey.includes("-");
      const alsoInAnotherSubSector = foundSectors.some(
        (sector) => sector.includes("-") && sector !== groupKey,
      );
      if (isSelectedSubSector && alsoInAnotherSubSector) return;

      if (!grouped[groupKey]) grouped[groupKey] = [];
      if (
        !grouped[groupKey].some(
          ([existingSym]) =>
            String(existingSym || "").toLowerCase() === normalizedSym,
        )
      ) {
        grouped[groupKey].push([sym, pat]);
      }
    });
  });

  // Sort symbols within each group by their array index in SectorMap
  Object.keys(grouped).forEach((sectorKey) => {
    if (SectorMap[sectorKey]) {
      grouped[sectorKey].sort(([symA], [symB]) => {
        const indexA = SectorMap[sectorKey].indexOf(symA);
        const indexB = SectorMap[sectorKey].indexOf(symB);
        return indexA - indexB;
      });
    }
  });

  return grouped;
};

/**
 * Sorts symbol entries by pattern strength
 * @param {Array} symbolEntries - Array of [symbol, pattern] tuples
 * @returns {Array} - Sorted array of [symbol, pattern] tuples
 */
export const sortSymbolsByStrength = (symbolEntries) => {
  return symbolEntries.sort(([, a], [, b]) => {
    // Handle both string patterns and object patterns
    const patternA = typeof a === "string" ? a : a?.pattern || "";
    const patternB = typeof b === "string" ? b : b?.pattern || "";
    const pa = formatPattern(patternA);
    const pb = formatPattern(patternB);

    const aHasVV = pa.includes("VV");
    const bHasVV = pb.includes("VV");
    if (aHasVV !== bHasVV) return bHasVV ? 1 : -1;

    const aHasV = pa.includes("V");
    const bHasV = pb.includes("V");
    if (aHasV !== bHasV) return bHasV ? 1 : -1;

    const aHasSS = pa.includes("SS");
    const bHasSS = pb.includes("SS");
    if (aHasSS !== bHasSS) return bHasSS ? 1 : -1;

    const aHasS = pa.includes("S");
    const bHasS = pb.includes("S");
    if (aHasS !== bHasS) return bHasS ? 1 : -1;

    const aHasSmallS = pa.includes("s");
    const bHasSmallS = pb.includes("s");
    if (aHasSmallS !== bHasSmallS) return bHasSmallS ? 1 : -1;

    const aHasSmallV = pa.includes("v");
    const bHasSmallV = pb.includes("v");
    if (aHasSmallV !== bHasSmallV) return bHasSmallV ? 1 : -1;

    return pb.length - pa.length;
  });
};

/**
 * Filters duplicate rendered sector groups whose symbol membership is identical.
 * Keep the first group in the already-sorted list; callers should use this as
 * the final display step so it does not affect grouping or sorting behavior.
 */
export const filterIdenticalSymbolGroups = (groupEntries) => {
  const seen = new Set();
  return groupEntries.filter(([, entries]) => {
    const signature = entries
      .map(([symbol]) => String(symbol || "").toLowerCase())
      .sort()
      .join("\u0000");
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
};

/**
 * Counts the number of entries in a group that contain strong patterns (SS, S, VV, V)
 * @param {Array} groupEntries - Array of [symbol, pattern] tuples in a group
 * @returns {number} - Count of entries with strong patterns
 */
const countStrongPatterns = (groupEntries) => {
  return groupEntries.reduce((count, [, patternData]) => {
    // Handle both string patterns and object patterns
    const pattern =
      typeof patternData === "string"
        ? patternData
        : patternData?.pattern || "";
    const formattedPattern = formatPattern(pattern);
    const hasStrongPattern =
      formattedPattern.includes("SS") ||
      formattedPattern.includes("S") ||
      formattedPattern.includes("VV") ||
      formattedPattern.includes("V");
    return count + (hasStrongPattern ? 1 : 0);
  }, 0);
};

/**
 * Gets the maximum percentage change in a group of symbols
 * @param {Array} groupEntries - Array of [symbol, pattern] tuples in a group
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @param {boolean} isPos - true for positive moves (use max), false for negative moves (use min)
 * @returns {number} - Maximum/minimum percentage change in the group
 */
const getGroupMaxPercentageChange = (
  groupEntries,
  currentGridMap,
  date,
  isPos,
) => {
  const percentageChanges = groupEntries.map(([sym]) =>
    getSymbolPercentageChange(sym, currentGridMap, date),
  );

  if (percentageChanges.length === 0) return 0;

  return isPos
    ? Math.max(...percentageChanges) // For positive moves, use the biggest gain
    : Math.min(...percentageChanges); // For negative moves, use the biggest loss
};

/**
 * Sorts grouped sectors by the biggest percentage change of symbols in each group
 * Groups with bigger percentage changes appear first
 * @param {Object} grouped - Grouped object from groupSymbolsBySector
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @param {boolean} isPos - true for positive moves, false for negative moves
 * @returns {Array} - Array of [sectorKey, sectorSymbols] sorted by biggest percentage change
 */
export const sortGroupsByStrength = (grouped, currentGridMap, date, isPos) => {
  return Object.entries(grouped).sort(([, entriesA], [, entriesB]) => {
    const maxChangeA = getGroupMaxPercentageChange(
      entriesA,
      currentGridMap,
      date,
      isPos,
    );
    const maxChangeB = getGroupMaxPercentageChange(
      entriesB,
      currentGridMap,
      date,
      isPos,
    );

    if (isPos) {
      return maxChangeB - maxChangeA; // Descending order (biggest gains first)
    } else {
      return maxChangeA - maxChangeB; // Ascending order (biggest losses first)
    }
  });
};

/**
 * Gets percentage change for a symbol from currentGridMap data (matches KVChildRow logic)
 * @param {string} symbol - Symbol to get percentage change for
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @returns {number} - Percentage change, or 0 if no data
 */
export const getSymbolPercentageChange = (symbol, currentGridMap, date) => {
  const s = (symbol || "").toLowerCase();
  const ticks = currentGridMap?.[s] || currentGridMap?.[symbol] || [];

  let today, prev;
  if (date && ticks.length) {
    const idx = ticks.findIndex((t) => t.timestamp === date || t.date === date);
    if (idx >= 0) {
      today = ticks[idx];
      prev = ticks[idx + 1];
    }
  } else {
    today = ticks[0];
    prev = ticks[1];
  }

  const hasData = !!(
    today &&
    prev &&
    Number.isFinite(+today.close) &&
    Number.isFinite(+prev.close)
  );

  if (!hasData) return 0;

  const close = +today.close;
  const prevClose = +prev.close;
  const delta = close - prevClose;
  const pct = hasData && prevClose !== 0 ? (delta / prevClose) * 100 : null;

  return pct || 0;
};

/**
 * Filters symbol entries by percentage change threshold
 * @param {Array} symbolEntries - Array of [symbol, pattern] tuples
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @param {number} threshold - Percentage threshold (default 10%)
 * @returns {Array} - Filtered array of [symbol, pattern] tuples
 */
export const filterSymbolsByPercentageThreshold = (
  symbolEntries,
  currentGridMap,
  date,
  threshold = 5,
) => {
  return symbolEntries.filter(([symbol]) => {
    const pct = Math.abs(
      getSymbolPercentageChange(symbol, currentGridMap, date),
    );
    return pct >= threshold;
  });
};

export const filterSymbolsByMarketCap = (
  symbolEntries,
  marketCapMap,
  threshold = 40,
) => {
  return symbolEntries.filter(([symbol]) => {
    const sym = (symbol || "").toString();
    const marketCap =
      marketCapMap?.[sym] ??
      marketCapMap?.[sym.toLowerCase()] ??
      marketCapMap?.[sym.toUpperCase()];
    return !(marketCap > 0 && marketCap < threshold);
  });
};

/**
 * Sorts sector symbols by percentage change
 * @param {Array} sectorSymbols - Array of [symbol, pattern] tuples
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @param {boolean} descending - true for descending (biggest gains first), false for ascending
 * @returns {Array} - Sorted array of [symbol, pattern] tuples
 */
export const sortSectorSymbolsByPercentChange = (
  sectorSymbols,
  currentGridMap,
  date,
  descending = true,
) => {
  return sectorSymbols.sort(([symA], [symB]) => {
    const pctA = getSymbolPercentageChange(symA, currentGridMap, date);
    const pctB = getSymbolPercentageChange(symB, currentGridMap, date);
    return descending ? pctB - pctA : pctA - pctB;
  });
};

/**
 * Gets the best matching sector for a symbol, prioritizing longer/more specific sector names
 * Uses the same logic as findMatchingSectorSymbols but returns the sector name
 * @param {string} symbol - Symbol to find sector for
 * @param {string} currentSector - Current selected sector for context
 * @returns {string|null} - Best matching sector name
 */
export const getBestSectorForSymbol = (symbol, currentSector = null) => {
  if (!symbol) return null;

  const sym = symbol.toLowerCase();
  const matchingSectors = [];

  // Find all sectors that contain this symbol
  for (const [sectorKey, sectorSymbols] of Object.entries(SectorMap)) {
    if (Array.isArray(sectorSymbols) && sectorSymbols.includes(sym)) {
      matchingSectors.push(sectorKey);
    }
  }

  if (matchingSectors.length === 0) return null;
  if (matchingSectors.length === 1) return matchingSectors[0];

  // If current sector contains this symbol, prefer it
  if (currentSector && matchingSectors.includes(currentSector)) {
    return currentSector;
  }

  // Otherwise, return the longest (most specific) sector name
  return matchingSectors.reduce((longest, current) =>
    current.length > longest.length ? current : longest,
  );
};

/**
 * Groups sectors by their prefix (e.g., "semi-conductor" and "semi-etf" both have "semi-" prefix)
 * @param {Array} groupEntries - Array of [sectorKey, sectorSymbols] entries
 * @returns {Object} - Object where keys are prefixes and values are arrays of matching groups
 */
const groupSectorsByPrefix = (groupEntries) => {
  const prefixGroups = {};

  groupEntries.forEach(([sectorKey, sectorSymbols]) => {
    // Extract prefix before the first dash, or use the whole key if no dash
    const dashIndex = sectorKey.indexOf("-");
    let prefix;

    if (dashIndex > 0) {
      // Has a dash, use prefix + dash (e.g., "semi-conductor" -> "semi-")
      prefix = sectorKey.substring(0, dashIndex + 1);
    } else {
      // No dash, check if this could be a root prefix
      // Look for other sectors that start with this key + dash
      const isPrefix = groupEntries.some(
        ([otherKey]) =>
          otherKey !== sectorKey && otherKey.startsWith(sectorKey + "-"),
      );

      if (isPrefix) {
        // This is a root prefix (e.g., "semi" when "semi-conductor" exists)
        prefix = sectorKey + "-";
      } else {
        // Standalone sector
        prefix = sectorKey;
      }
    }

    if (!prefixGroups[prefix]) {
      prefixGroups[prefix] = [];
    }
    prefixGroups[prefix].push([sectorKey, sectorSymbols]);
  });

  return prefixGroups;
};

/**
 * Sorts grouped sectors by their common prefixes, then by strength within each prefix group
 * Groups with the same prefix (like "semi-") are kept adjacent, then sorted by biggest percentage change
 * @param {Object} grouped - Grouped object from groupSymbolsBySector
 * @param {Object} currentGridMap - Store currentGridMap containing price data
 * @param {string} date - Formatted date for historical lookup
 * @param {boolean} isPos - true for positive moves, false for negative moves
 * @returns {Array} - Array of [sectorKey, sectorSymbols] sorted by prefix groups then by strength
 */
export const sortGroupsBySector = (grouped, currentGridMap, date, isPos) => {
  const groupEntries = Object.entries(grouped);
  const prefixGroups = groupSectorsByPrefix(groupEntries);

  // Sort each prefix group by strength (percentage change)
  const sortedPrefixGroups = Object.entries(prefixGroups).map(
    ([prefix, groups]) => {
      const sortedGroups = groups.sort(([, entriesA], [, entriesB]) => {
        const maxChangeA = getGroupMaxPercentageChange(
          entriesA,
          currentGridMap,
          date,
          isPos,
        );
        const maxChangeB = getGroupMaxPercentageChange(
          entriesB,
          currentGridMap,
          date,
          isPos,
        );

        if (isPos) {
          return maxChangeB - maxChangeA; // Descending order (biggest gains first)
        } else {
          return maxChangeA - maxChangeB; // Ascending order (biggest losses first)
        }
      });

      return [prefix, sortedGroups];
    },
  );

  // Sort prefix groups by the best percentage change within each prefix group
  sortedPrefixGroups.sort(([, groupsA], [, groupsB]) => {
    const bestChangeA = Math.max(
      ...groupsA.map(([, entries]) =>
        getGroupMaxPercentageChange(entries, currentGridMap, date, isPos),
      ),
    );
    const bestChangeB = Math.max(
      ...groupsB.map(([, entries]) =>
        getGroupMaxPercentageChange(entries, currentGridMap, date, isPos),
      ),
    );

    if (isPos) {
      return bestChangeB - bestChangeA; // Descending order (biggest gains first)
    } else {
      return bestChangeA - bestChangeB; // Ascending order (biggest losses first)
    }
  });

  // Flatten the result
  const result = [];
  sortedPrefixGroups.forEach(([, groups]) => {
    groups.forEach((groupEntry) => {
      result.push(groupEntry);
    });
  });

  return result;
};
